"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
function KitchenForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="223210" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
                <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <svg className="size-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="text-2xl font-default text-foreground uppercase tracking-wider">Message Received</h3>
                <p className="text-muted-foreground font-default">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                    Send Another Message
                </Button>
            </div>;
  }
  const inputClasses = "border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent shadow-none text-foreground placeholder:text-muted-foreground/30 text-lg py-3 font-default";
  return <Form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs font-semibold uppercase tracking-widest text-primary">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Sultan Ahmed" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs font-semibold uppercase tracking-widest text-primary">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="sultan@sultanigrill.com" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs font-semibold uppercase tracking-widest text-primary">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="(972) 600-9309" className={inputClasses} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs mt-1 text-muted-foreground/50">Optional for catering follow-ups</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs font-semibold uppercase tracking-widest text-primary">Inquiry Type</FormLabel>
                            <FormControl>
                                <Input placeholder="e.g., Large Party Catering" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "A message is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs font-semibold uppercase tracking-widest text-primary">Your Message</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Tell us about your event or feedback..." className={`${inputClasses} min-h-[150px] resize-none`} {...field} />
                        </FormControl>
                        <FormDescription className="text-xs mt-1 text-muted-foreground/50">Include dates, guest counts, or specific dietary needs</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
                    <p className="text-destructive text-sm font-default">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-xl font-default uppercase tracking-widest shadow-2xl transition-all hover:scale-[1.02]">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : 'Send Message'}
            </Button>
        </Form>;
}
export default function ReachOutToTheKitchen() {
  return <section id="drop-a-line" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-12 gap-16 items-start">
                    
                    {}
                    <div className="lg:col-span-7 flex flex-col justify-center">
                        <div className="mb-12">
                            <h2 className="text-6xl md:text-7xl lg:text-8xl font-serif text-foreground mb-6 tracking-tight uppercase">
                                Get In Touch
                            </h2>
                            <p className="text-xl text-muted-foreground max-w-xl font-default">
                                Whether you're planning a massive event or just want to talk spice levels, the Sultani team is ready to serve.
                            </p>
                        </div>

                        <FormProvider formKey="drop_a_line_form_contact" formId="drop_a_line_form_contact" sectionName="ReachOutToTheKitchen" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Message received. We'll get back to you faster than a smash burger hits the grill.">
                            <KitchenForm />
                        </FormProvider>
                    </div>

                    {}
                    <div className="lg:col-span-5 hidden md:flex flex-col gap-6 h-full">
                        <div className="relative w-full h-[400px] rounded-none overflow-hidden shadow-4xl border border-border">
                            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648254/img/sultani_grill_interior.png" alt="Moody interior of Sultani Grill" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105 grayscale-[0.3] hover:grayscale-0" />
                        </div>
                        <div className="grid grid-cols-2 gap-6 h-[250px]">
                            <div className="relative w-full h-full rounded-none overflow-hidden shadow-2xl border border-border">
                                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648254/img/sultani_smash_burger.png" alt="Sultani signature smash burger" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                            </div>
                            <div className="relative w-full h-full rounded-none overflow-hidden shadow-2xl border border-border">
                                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648254/img/sultani_karahi_prep.png" alt="Fresh karahi preparation" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}